import { defineComponent } from 'vue';
import { QSpinner } from 'quasar';
import BaseComponent from './BaseComponentMixin.jsx';
import utils from '../../Shared/utils.jsx';
import api from '@/Services/api';
import token from '@/Services/token';
import { createHostBridgeHandler } from '@callcorpacd/platform-bridge';
import theme from '@/plugins/theme';
import eventCoordinator from '@/Services/serverEventCoordinator';
import EventBus from '@/Application/event-bus';

// allow-downloads is required for guests that save files; without it the browser
// blocks anchor/blob downloads from the frame without surfacing an error.
// allow-popups is required for guests that open external links (e.g. Azure commit
// pages) via target=_blank / window.open from the sandboxed iframe.
const DEFAULT_SANDBOX = 'allow-scripts allow-forms allow-same-origin allow-downloads allow-popups';

/** Relative ESM import/export specifiers: from './x' or import('./x') */
const RELATIVE_MODULE_SPECIFIER_RE = /((?:from|import)\s*\(?\s*)(['"])(\.[^'"]+)\2/g;

/** Relative CSS url(...) references */
const RELATIVE_CSS_URL_RE = /url\(\s*(['"]?)(\.\/[^'")]+|\.\.\/[^'")]+)\1\s*\)/g;

function originFromUrl(url) {
    if (!url)
        return null;
    try {
        if (url.startsWith('blob:'))
            return typeof window !== 'undefined' ? window.location.origin : null;
        return new URL(url, typeof window !== 'undefined' ? window.location.href : undefined).origin;
    }
    catch {
        return null;
    }
}

function dirnamePath(path) {
    const normalized = String(path || '').replace(/\\/g, '/').replace(/\/+$/, '');
    const idx = normalized.lastIndexOf('/');
    return idx < 0 ? '' : normalized.slice(0, idx);
}

function resolveRelativePath(fromPath, relativeSpec) {
    const baseDir = dirnamePath(fromPath);
    const parts = [...baseDir.split('/').filter(Boolean), ...String(relativeSpec).split('/')];
    const stack = [];
    for (const part of parts) {
        if (!part || part === '.')
            continue;
        if (part === '..')
            stack.pop();
        else
            stack.push(part);
    }
    return stack.join('/');
}

function mimeForPath(path) {
    const lower = String(path).toLowerCase();
    if (lower.endsWith('.html') || lower.endsWith('.htm'))
        return 'text/html';
    if (lower.endsWith('.js') || lower.endsWith('.mjs') || lower.endsWith('.cjs'))
        return 'text/javascript';
    if (lower.endsWith('.css'))
        return 'text/css';
    if (lower.endsWith('.json'))
        return 'application/json';
    if (lower.endsWith('.svg'))
        return 'image/svg+xml';
    if (lower.endsWith('.png'))
        return 'image/png';
    if (lower.endsWith('.jpg') || lower.endsWith('.jpeg'))
        return 'image/jpeg';
    if (lower.endsWith('.woff2'))
        return 'font/woff2';
    if (lower.endsWith('.woff'))
        return 'font/woff';
    return 'application/octet-stream';
}

function isRelativeAssetSpec(spec) {
    if (!spec || typeof spec !== 'string')
        return false;
    if (/^(https?:|blob:|data:|\/\/)/i.test(spec))
        return false;
    return spec.startsWith('./') || spec.startsWith('../');
}

/** Local Vite guest embeds — cert issues only surface as a blank iframe. */
function isLocalDevHttpsUrl(url) {
    if (!url || typeof url !== 'string')
        return false;
    try {
        const parsed = new URL(url);
        return parsed.protocol === 'https:'
            && (parsed.hostname === 'localhost' || parsed.hostname === '127.0.0.1');
    }
    catch {
        return false;
    }
}

/**
 * Portal api.post flattens to `response.data.Result`. For Raw/UI document saves,
 * Result is often boolean `true` while Id/Rev live on Output — guests then never
 * see the new DocumentID. Bridge mutations return the full data envelope instead.
 * @param {unknown} data
 */
function bridgeApiPayload(data) {
    if (!data || typeof data !== 'object' || Array.isArray(data))
        return data;
    if (Object.prototype.hasOwnProperty.call(data, 'Output') && typeof data.Result === 'boolean')
        return data;
    if (Object.prototype.hasOwnProperty.call(data, 'Result'))
        return data.Result;
    return data;
}

/**
 * Api surface for platform-bridge host: GETs keep portal flatten; mutating verbs
 * return envelopes so Output.Id survives for document creates.
 * @param {typeof api} portalApi
 */
function createBridgeApi(portalApi) {
    const mutate = (method) => (url, data) =>
        portalApi.apiRequest({
            method,
            url,
            data,
            flatten: false,
        }).then((response) => bridgeApiPayload(response?.data ?? response));

    return {
        get: (url) => portalApi.get(url),
        cachedGet: (url) => portalApi.cachedGet(url),
        delete_: (url) => portalApi.delete_(url),
        post: mutate('POST'),
        put: mutate('PUT'),
        patch: mutate('PATCH'),
    };
}

const LOCAL_DEV_HELP_DELAY_MS = 2500;

export default defineComponent({
    name: 'sandboxed-app',
    mixins: [BaseComponent],
    components: { QSpinner },
    data() {
        return {
            uniqueKey: utils.generateUUID(),
            sourceUrlEval: null,
            ownerIdEval: null,
            contentPathEval: null,
            sandboxNameEval: null,
            sourceKindEval: null,
            paramsEval: null,
            routeEval: null,
            rteventsourceEval: null,
            resolvedSrc: '',
            contentOrigin: null,
            resolving: true,
            loadError: null,
            bridgeHandler: null,
            messageListener: null,
            themeUnsubscribe: null,
            blobUrlsToRevoke: [],
            iframeRef: null,
            guestReady: false,
            showLocalDevHelp: false,
            localDevHelpTimer: null,
            localDevHelpGeneration: 0,
            iframeReloadKey: 0,
        };
    },
    computed: {
        /**
         * RTEventHandler accessor (Views / AddView / GetNamedListForRealTime).
         * Defaults to GlobalVars.RTData when ControlData.RTEventSource is unset.
         */
        rtEventSource() {
            const raw = this.controlData?.RTEventSource;
            if (raw) {
                if (!this.rteventsourceEval)
                    this.rteventsourceEval = utils.compileExpression(this, raw);
                return this.rteventsourceEval ? utils.evaluate(this.rteventsourceEval, this) : null;
            }
            return this.GlobalVars?.RTData || null;
        },
        allowAllRealtimeFeeds() {
            return !!this.controlData?.AllowAllRealtimeFeeds;
        },
        allowedRealtimeFeeds() {
            const feeds = this.controlData?.AllowedRealtimeFeeds;
            if (!Array.isArray(feeds))
                return [];
            return feeds
                .filter((f) => f && (f.kind || f.Kind) && (f.name || f.Name))
                .map((f) => ({
                    kind: String(f.kind || f.Kind),
                    name: String(f.name || f.Name),
                }));
        },
        allowAllServerEvents() {
            return !!this.controlData?.AllowAllServerEvents;
        },
        allowedServerEventNames() {
            const names = this.controlData?.AllowedServerEventNames;
            if (!Array.isArray(names))
                return [];
            return names.map((n) => String(n)).filter(Boolean);
        },
        allowAllBusEvents() {
            return !!this.controlData?.AllowAllBusEvents;
        },
        allowedBusEvents() {
            const names = this.controlData?.AllowedBusEvents;
            if (!Array.isArray(names))
                return [];
            return names.map((n) => String(n)).filter(Boolean);
        },
        sourceKind() {
            const raw = this.controlData?.SourceKind;
            if (!raw)
                return 'Url';
            if (!this.sourceKindEval)
                this.sourceKindEval = utils.compile(this, raw);
            const value = this.sourceKindEval ? utils.evaluate(this.sourceKindEval, this) : raw;
            return String(value || 'Url');
        },
        ownerId() {
            if (!this.controlData?.OwnerId)
                return '';
            if (!this.ownerIdEval)
                this.ownerIdEval = utils.compile(this, this.controlData.OwnerId);
            return this.ownerIdEval ? utils.evaluate(this.ownerIdEval, this) : '';
        },
        contentPath() {
            if (!this.controlData?.ContentPath)
                return '';
            if (!this.contentPathEval)
                this.contentPathEval = utils.compile(this, this.controlData.ContentPath);
            return this.contentPathEval ? utils.evaluate(this.contentPathEval, this) : '';
        },
        sandboxName() {
            if (!this.controlData?.SandboxName)
                return '';
            if (!this.sandboxNameEval)
                this.sandboxNameEval = utils.compile(this, this.controlData.SandboxName);
            return this.sandboxNameEval ? utils.evaluate(this.sandboxNameEval, this) : '';
        },
        configuredUrl() {
            if (!this.controlData?.Url)
                return '';
            if (!this.sourceUrlEval)
                this.sourceUrlEval = utils.compile(this, this.controlData.Url);
            return this.sourceUrlEval ? utils.evaluate(this.sourceUrlEval, this) : '';
        },
        /**
         * Evaluated ControlData.Params for platform.context.params.
         * Top-level primitives are coerced to strings; nested objects/arrays are JSON-cloned.
         * Values may use portal interpolations ({{ }} / {# #}).
         */
        appParams() {
            const raw = this.controlData?.Params;
            if (!raw) // || typeof raw !== 'object' || Array.isArray(raw))
                return {};

            if (!this.paramsEval)
                this.paramsEval = utils.compileObject(this, raw);

            const evaluated = this.paramsEval
                ? utils.evaluateObject(this.paramsEval, this)
                : raw;

            if (!evaluated || typeof evaluated !== 'object' || Array.isArray(evaluated))
                return {};

            const out = {};
            for (const key of Object.keys(evaluated)) {
                const value = evaluated[key];
                if (value === undefined || value === null) {
                    out[key] = '';
                }
                else if (typeof value === 'object') {
                    try {
                        out[key] = JSON.parse(JSON.stringify(value));
                    }
                    catch {
                        out[key] = String(value);
                    }
                }
                else {
                    out[key] = String(value);
                }
            }
            return out;
        },
        /**
         * Evaluated ControlData.Route — string for platform.context.route (Shared Panel SPA path).
         * May use portal interpolations ({{ }} / {# #}).
         */
        appRoute() {
            const raw = this.controlData?.Route;
            if (raw === undefined || raw === null || raw === '')
                return '';
            if (!this.routeEval)
                this.routeEval = utils.compile(this, raw);
            const value = this.routeEval ? utils.evaluate(this.routeEval, this) : raw;
            return value === undefined || value === null ? '' : String(value);
        },
        allowedApiPrefixes() {
            const prefixes = this.controlData?.AllowedApiPrefixes;
            if (!Array.isArray(prefixes))
                return [];
            return prefixes.map((p) => String(p));
        },
        sandboxAttr() {
            return this.controlData?.Sandbox || DEFAULT_SANDBOX;
        },
        style() {
            return {
                ...this.sizeStyle,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column',
            };
        },
    },
    watch: {
        sourceKind() { this.resolveAndLoad(); },
        ownerId() { this.resolveAndLoad(); },
        contentPath() { this.resolveAndLoad(); },
        sandboxName() { this.resolveAndLoad(); },
        configuredUrl() { this.resolveAndLoad(); },
        appParams: {
            deep: true,
            handler() {
                this.pushContextToGuest();
            },
        },
        appRoute() {
            this.pushContextToGuest();
        },
    },
    methods: {
        async preRenderComplete() {
            this.attachBridge();
            await this.resolveAndLoad();
            this.finishRenderHandler(this);
        },
        Refresh() {
            // Force the sandbox i-frame to reload
            this.uniqueKey = utils.generateUUID();
        },
        attachBridge() {
            this.detachBridge();

            this.bridgeHandler = createHostBridgeHandler({
                getAllowedOrigin: () => {
                    const origins = [];
                    if (this.contentOrigin)
                        origins.push(this.contentOrigin);
                    // PlatformFile blob: guests share the portal origin
                    if (typeof window !== 'undefined')
                        origins.push(window.location.origin);
                    return origins;
                },
                // Bind this host to our iframe only — sibling SandboxedApps share origin.
                getGuestWindow: () => this.iframeRef?.contentWindow || null,
                getAllowedApiPrefixes: () => this.allowedApiPrefixes,
                getContextParams: () => this.appParams,
                getContextRoute: () => this.appRoute,
                getTheme: () => ({
                    colors: { ...(theme.getActiveTheme() || {}) },
                    name: theme.getActiveThemeName?.() || 'defaultTheme',
                }),
                api: createBridgeApi(api),
                getRealtimeSource: () => this.rtEventSource,
                getAllowAllRealtimeFeeds: () => this.allowAllRealtimeFeeds,
                getAllowedRealtimeFeeds: () => this.allowedRealtimeFeeds,
                watchRealtimeView: (viewKey, callback) => this.watchRealtimeView(viewKey, callback),
                ui: {
                    translate: (text, isToolTip) => this.Translate(text, isToolTip),
                    hasAccess: (id, name) => utils.document_cache.hasAccess(name || '', { ID: id, Enabled: true }),
                },
                events: {
                    runEventActions: (name, data) => this.runEventActions(name, data),
                },
                getAllowAllServerEvents: () => this.allowAllServerEvents,
                getAllowedServerEventNames: () => this.allowedServerEventNames,
                subscribeServerEvents: (opts, onEvent) => this.subscribeBridgeServerEvents(opts, onEvent),
                getAllowAllBusEvents: () => this.allowAllBusEvents,
                getAllowedBusEvents: () => this.allowedBusEvents,
                subscribeBus: (opts, onPayload) => this.subscribeBridgeBus(opts, onPayload),
                onReady: () => {
                    this.onGuestReady();
                },
                onError: (error, context) => {
                    utils.warn('SandboxedApp bridge API error', { error, context });
                },
            });

            this.messageListener = (event) => this.bridgeHandler.handleMessage(event);
            window.addEventListener('message', this.messageListener);

            if (typeof theme.subscribe === 'function') {
                this.themeUnsubscribe = theme.subscribe(() => {
                    if (this.bridgeHandler && typeof this.bridgeHandler.pushThemeUpdate === 'function')
                        this.bridgeHandler.pushThemeUpdate();
                });
            }
        },
        /**
         * Framework-free watch hook for the bridge host: observe RT Views[viewKey].
         * @param {string} viewKey
         * @param {(value: unknown) => void} callback
         * @returns {() => void} unwatch
         */
        watchRealtimeView(viewKey, callback) {
            if (!viewKey || typeof callback !== 'function')
                return () => {};

            const stop = this.$watch(
                () => {
                    const src = this.rtEventSource;
                    return src && src.Views ? src.Views[viewKey] : undefined;
                },
                (value) => {
                    try {
                        callback(value);
                    }
                    catch (e) {
                        utils.warn('SandboxedApp realtime view watcher error', e);
                    }
                },
                { deep: true }
            );

            return typeof stop === 'function' ? stop : () => {};
        },
        /**
         * Wire platform.serverEvents through serverEventCoordinator.
         * @param {{ sessionId: string, names: string[], uniqueId: string }} opts
         * @param {(raw: unknown) => void} onEvent
         * @returns {() => void}
         */
        subscribeBridgeServerEvents(opts, onEvent) {
            const sessionId = opts?.sessionId;
            const uniqueId = opts?.uniqueId || `bridge:${this.uniqueKey}`;
            const names = Array.isArray(opts?.names) ? opts.names : [];
            const eventNames = names.length ? names.join(',') : '';

            if (!sessionId || typeof onEvent !== 'function')
                return () => {};

            eventCoordinator.subscribeForSessionEvents(sessionId, uniqueId, eventNames, onEvent);

            return () => {
                eventCoordinator.unsubscribeForSessionEvents(sessionId, uniqueId, eventNames || undefined);
            };
        },
        /**
         * Wire platform.bus through the portal EventBus.
         * @param {{ name: string }} opts
         * @param {(...args: unknown[]) => void} onPayload
         * @returns {() => void}
         */
        subscribeBridgeBus(opts, onPayload) {
            const name = opts?.name;
            if (!name || typeof onPayload !== 'function')
                return () => {};

            const handler = (...args) => {
                try {
                    onPayload(...args);
                }
                catch (e) {
                    utils.warn('SandboxedApp bus handler error', e);
                }
            };

            EventBus.$on(name, handler);
            return () => {
                EventBus.$off(name, handler);
            };
        },
        detachBridge() {
            if (this.themeUnsubscribe) {
                this.themeUnsubscribe();
                this.themeUnsubscribe = null;
            }
            if (this.messageListener) {
                window.removeEventListener('message', this.messageListener);
                this.messageListener = null;
            }
            if (this.bridgeHandler) {
                this.bridgeHandler.dispose();
                this.bridgeHandler = null;
            }
        },
        pushContextToGuest() {
            if (this.bridgeHandler && typeof this.bridgeHandler.pushContextUpdate === 'function')
                this.bridgeHandler.pushContextUpdate(this.appParams, this.appRoute);
        },
        trackBlobUrl(url) {
            if (url)
                this.blobUrlsToRevoke.push(url);
            return url;
        },
        revokeBlobUrls() {
            for (const url of this.blobUrlsToRevoke)
                URL.revokeObjectURL(url);
            this.blobUrlsToRevoke = [];
        },
        buildMassStorageUrl(ownerId, contentPath) {
            const owner = encodeURIComponent(String(ownerId).replace(/^\/+|\/+$/g, ''));
            const path = String(contentPath).replace(/^\/+/, '');
            const base = token.GetApiBaseURL().replace(/\/+$/, '');
            return `${base}/Apps/GetFileMassStorageBinary/${owner}/${path}`;
        },
        buildPlatformFileUrl(contentPath) {
            const path = String(contentPath).replace(/^\/+/, '');
            const base = token.GetApiBaseURL().replace(/\/+$/, '');
            return `${base}/Apps/FileBin/${path}`;
        },
        buildSandboxedAppFileUrl(sandboxId, contentPath) {
            const path = String(contentPath).replace(/^\/+/, '');
            const id = encodeURIComponent(String(sandboxId));
            const base = token.GetApiBaseURL().replace(/\/+$/, '');
            return `${base}/Apps/SandboxedApp/File/${path}?ID=${id}`;
        },
        async fetchAuthenticated(url) {
            const response = await fetch(url, {
                method: 'GET',
                mode: 'cors',
                credentials: 'include',
                headers: {
                    access_token: token.AccessToken() || '',
                },
            });

            if (!response.ok) {
                const text = await response.text().catch(() => '');
                throw new Error(`Failed to load partner content (${response.status}): ${text.slice(0, 200)}`);
            }

            return response;
        },
        async fetchAuthenticatedText(url) {
            const response = await this.fetchAuthenticated(url);
            return response.text();
        },
        async fetchAuthenticatedBytes(url) {
            const response = await this.fetchAuthenticated(url);
            return response.arrayBuffer();
        },
        /**
         * Authenticated file APIs cannot be used as a raw iframe src (no access_token header).
         * Fetch the entry HTML + relative Vite assets (JS/CSS/etc.) with the portal token,
         * rewrite relative URLs to blob: URLs, and load the rewritten HTML as a blob document.
         * @param {string} entryPath relative entry path (e.g. index.html or PartnerContent/.../index.html)
         * @param {(assetPath: string) => string} buildUrl absolute authenticated URL for a path
         */
        async materializeAuthenticatedBundle(entryPath, buildUrl) {
            const entry = String(entryPath).replace(/^\/+/, '');
            const blobByPath = new Map();
            const pending = new Map();

            const loadAsset = async (assetPath) => {
                if (blobByPath.has(assetPath))
                    return blobByPath.get(assetPath);
                if (pending.has(assetPath))
                    return pending.get(assetPath);

                const promise = (async () => {
                    const url = buildUrl(assetPath);
                    const mime = mimeForPath(assetPath);
                    let blobUrl;

                    if (mime === 'text/javascript') {
                        const source = await this.fetchAuthenticatedText(url);
                        const rewritten = await this.rewriteRelativeModuleSpecifiers(source, assetPath, loadAsset);
                        blobUrl = this.trackBlobUrl(URL.createObjectURL(new Blob([rewritten], { type: mime })));
                    }
                    else if (mime === 'text/css') {
                        const source = await this.fetchAuthenticatedText(url);
                        const rewritten = await this.rewriteRelativeCssUrls(source, assetPath, loadAsset);
                        blobUrl = this.trackBlobUrl(URL.createObjectURL(new Blob([rewritten], { type: mime })));
                    }
                    else {
                        const bytes = await this.fetchAuthenticatedBytes(url);
                        blobUrl = this.trackBlobUrl(URL.createObjectURL(new Blob([bytes], { type: mime })));
                    }

                    blobByPath.set(assetPath, blobUrl);
                    pending.delete(assetPath);
                    return blobUrl;
                })();

                pending.set(assetPath, promise);
                return promise;
            };

            const entryUrl = buildUrl(entry);
            let html = await this.fetchAuthenticatedText(entryUrl);
            html = await this.rewriteHtmlDocument(html, entry, loadAsset);

            return this.trackBlobUrl(URL.createObjectURL(new Blob([html], { type: 'text/html' })));
        },
        async materializePlatformFileBundle(entryPath) {
            return this.materializeAuthenticatedBundle(entryPath, (p) => this.buildPlatformFileUrl(p));
        },
        async rewriteRelativeModuleSpecifiers(source, fromPath, loadAsset) {
            const matches = [...String(source).matchAll(RELATIVE_MODULE_SPECIFIER_RE)];
            if (matches.length === 0)
                return source;

            const replacements = [];
            for (const match of matches) {
                const prefix = match[1];
                const quote = match[2];
                const spec = match[3];
                const resolvedPath = resolveRelativePath(fromPath, spec);
                const blobUrl = await loadAsset(resolvedPath);
                replacements.push({
                    from: match[0],
                    to: `${prefix}${quote}${blobUrl}${quote}`,
                });
            }

            let rewritten = source;
            for (const { from, to } of replacements)
                rewritten = rewritten.replace(from, to);
            return rewritten;
        },
        async rewriteRelativeCssUrls(source, fromPath, loadAsset) {
            const matches = [...String(source).matchAll(RELATIVE_CSS_URL_RE)];
            if (matches.length === 0)
                return source;

            const replacements = [];
            for (const match of matches) {
                const quote = match[1] || '';
                const spec = match[2];
                const resolvedPath = resolveRelativePath(fromPath, spec);
                const blobUrl = await loadAsset(resolvedPath);
                replacements.push({
                    from: match[0],
                    to: `url(${quote}${blobUrl}${quote})`,
                });
            }

            let rewritten = source;
            for (const { from, to } of replacements)
                rewritten = rewritten.replace(from, to);
            return rewritten;
        },
        async rewriteHtmlDocument(html, entryPath, loadAsset) {
            let result = await this.rewriteHtmlTaggedAssets(html, entryPath, loadAsset);
            result = await this.rewriteHtmlModuleScripts(result, entryPath, loadAsset);
            return result;
        },
        async rewriteHtmlTaggedAssets(html, entryPath, loadAsset) {
            // Rewrite relative src/href on script, link, img, source (Vite dist uses ./assets/...).
            const tagRe = /<(script|link|img|source)(\s[^>]*?)(\/?)>/gi;
            const parts = [];
            let lastIndex = 0;
            let match;

            while ((match = tagRe.exec(html)) !== null) {
                parts.push(html.slice(lastIndex, match.index));
                const tag = match[1];
                let attrs = match[2] || '';
                const selfClose = match[3] || '';

                const attrName = tag.toLowerCase() === 'link' ? 'href' : 'src';
                // link also uses href; modulepreload uses href
                const attrRe = new RegExp(`\\b(${attrName}|href)\\s*=\\s*(['"])([^'"]+)\\2`, 'i');
                const attrMatch = attrs.match(attrRe);

                if (attrMatch && isRelativeAssetSpec(attrMatch[3])) {
                    const resolvedPath = resolveRelativePath(entryPath, attrMatch[3]);
                    const blobUrl = await loadAsset(resolvedPath);
                    attrs = attrs.replace(attrMatch[0], `${attrMatch[1]}=${attrMatch[2]}${blobUrl}${attrMatch[2]}`);
                    parts.push(`<${tag}${attrs}${selfClose}>`);
                }
                else {
                    parts.push(match[0]);
                }

                lastIndex = match.index + match[0].length;
            }

            parts.push(html.slice(lastIndex));
            return parts.join('');
        },
        async rewriteHtmlModuleScripts(html, entryPath, loadAsset) {
            const scriptRe = /<script(\s[^>]*)?>([\s\S]*?)<\/script>/gi;
            const parts = [];
            let lastIndex = 0;
            let match;

            while ((match = scriptRe.exec(html)) !== null) {
                parts.push(html.slice(lastIndex, match.index));
                const attrs = match[1] || '';
                const body = match[2] || '';
                const isModule = /\btype\s*=\s*['"]module['"]/i.test(attrs);
                const srcMatch = attrs.match(/\bsrc\s*=\s*(['"])([^'"]+)\1/i);

                // External scripts already rewritten by rewriteHtmlTaggedAssets (src → blob).
                // Still rewrite inline module bodies.
                if (!srcMatch && isModule && body.trim()) {
                    const rewrittenBody = await this.rewriteRelativeModuleSpecifiers(body, entryPath, loadAsset);
                    parts.push(`<script${attrs}>${rewrittenBody}</script>`);
                }
                else {
                    parts.push(match[0]);
                }

                lastIndex = match.index + match[0].length;
            }

            parts.push(html.slice(lastIndex));
            return parts.join('');
        },
        async resolveAndLoad() {
            this.loadError = null;
            this.resolving = true;
            this.resolvedSrc = '';
            this.contentOrigin = null;
            this.revokeBlobUrls();
            this.clearLocalDevHelpWatch();

            try {
                const kind = this.sourceKind;
                let src = '';

                if (kind === 'Url') {
                    src = this.configuredUrl || '';
                    if (!src)
                        throw new Error('SandboxedApp SourceKind=Url requires ControlData.Url');
                }
                else if (kind === 'MassStorage') {
                    if (this.configuredUrl) {
                        src = this.configuredUrl;
                    }
                    else {
                        if (!this.ownerId || !this.contentPath)
                            throw new Error('SandboxedApp SourceKind=MassStorage requires OwnerId and ContentPath (or Url)');
                        src = this.buildMassStorageUrl(this.ownerId, this.contentPath);
                    }
                }
                else if (kind === 'PlatformFile') {
                    if (this.configuredUrl && !this.contentPath) {
                        const text = await this.fetchAuthenticatedText(this.configuredUrl);
                        src = this.trackBlobUrl(URL.createObjectURL(new Blob([text], { type: mimeForPath(this.configuredUrl) || 'text/html' })));
                    }
                    else {
                        if (!this.contentPath)
                            throw new Error('SandboxedApp SourceKind=PlatformFile requires ContentPath');
                        src = await this.materializePlatformFileBundle(this.contentPath);
                    }
                }
                else if (kind === 'SandboxName') {
                    if (!this.sandboxName || !this.contentPath)
                        throw new Error('SandboxedApp SourceKind=SandboxName requires SandboxName and ContentPath');
                    src = await this.materializeAuthenticatedBundle(
                        this.contentPath,
                        (p) => this.buildSandboxedAppFileUrl(this.sandboxName, p),
                    );
                }
                else {
                    throw new Error(`SandboxedApp unknown SourceKind: ${kind}`);
                }

                this.resolvedSrc = src;
                this.contentOrigin = originFromUrl(src);
                this.startLocalDevHelpWatch(src);
            }
            catch (e) {
                this.resolvedSrc = '';
                this.contentOrigin = null;
                this.loadError = e?.message || String(e);
                utils.error('SandboxedApp resolve failed', e);
            }
            finally {
                this.resolving = false;
            }
        },
        onIframeRef(el) {
            this.iframeRef = el;
        },
        clearLocalDevHelpWatch() {
            if (this.localDevHelpTimer) {
                clearTimeout(this.localDevHelpTimer);
                this.localDevHelpTimer = null;
            }
            this.localDevHelpGeneration += 1;
            this.showLocalDevHelp = false;
            this.guestReady = false;
        },
        onGuestReady() {
            this.guestReady = true;
            this.showLocalDevHelp = false;
            if (this.localDevHelpTimer) {
                clearTimeout(this.localDevHelpTimer);
                this.localDevHelpTimer = null;
            }
        },
        async probeLocalDevReachable(url) {
            try {
                await fetch(url, { mode: 'no-cors', cache: 'no-store' });
                return true;
            }
            catch {
                return false;
            }
        },
        async startLocalDevHelpWatch(src) {
            this.clearLocalDevHelpWatch();
            if (this.sourceKind !== 'Url' || !isLocalDevHttpsUrl(src))
                return;

            const generation = this.localDevHelpGeneration;
            const reachable = await this.probeLocalDevReachable(src);
            if (generation !== this.localDevHelpGeneration || this.guestReady)
                return;

            // TLS/network failed (untrusted cert or server down). Wait briefly for a late
            // bridge handshake, then surface guided recovery instead of a blank iframe.
            if (reachable)
                return;

            this.localDevHelpTimer = setTimeout(() => {
                this.localDevHelpTimer = null;
                if (generation !== this.localDevHelpGeneration || this.guestReady)
                    return;
                this.showLocalDevHelp = true;
            }, LOCAL_DEV_HELP_DELAY_MS);
        },
        openLocalDevInTab() {
            if (this.resolvedSrc && typeof window !== 'undefined')
                window.open(this.resolvedSrc, '_blank', 'noopener,noreferrer');
        },
        retryLocalDev() {
            this.clearLocalDevHelpWatch();
            this.iframeReloadKey += 1;
            if (this.resolvedSrc)
                this.startLocalDevHelpWatch(this.resolvedSrc);
        },
        runEventActions(name, data) {
            if (this.controlData.Events) {
                const e = this.controlData.Events.find(a => a.Name == name || a.Name == '*');
                if (e)
                    utils.executeAndCompileAllActions(e.Actions, { Data: data, EventName: name }, this);
            }
        },
    },
    unmounted() {
        this.clearLocalDevHelpWatch();
        this.detachBridge();
        this.revokeBlobUrls();
    },
    render() {
        if (!this.todisplay)
            return null;

        try {
            if (this.loadError) {
                return (
                    <div
                        v-show={this.isvisible}
                        style={this.style}
                        class={{ 'c-SandboxedApp': true, [`c-name-${this.name || 'unnamed'}`]: true }}>
                        <div style="padding: 0.75rem; color: #a00;">SandboxedApp failed to load: {this.loadError}</div>
                    </div>
                );
            }

            return (
                <div
                    v-show={this.isvisible}
                    style={{ ...this.style, position: 'relative' }}
                    class={{ 'c-SandboxedApp': true, [`c-name-${this.name || 'unnamed'}`]: true }}
                    key={this.uniqueKey}
                >
                    {this.resolvedSrc ? (
                        <iframe
                            key={this.iframeReloadKey}
                            ref={this.onIframeRef}
                            style="flex-grow: 1; border: 0; width: 100%; min-height: 0;"
                            src={this.resolvedSrc}
                            sandbox={this.sandboxAttr}
                            title={this.name || 'SandboxedApp'}
                        />
                    ) : this.resolving ? (
                        <div
                            style={{
                                flexGrow: 1,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: '8rem',
                                color: '#666',
                            }}>
                            <QSpinner color="primary" size="48px" />
                        </div>
                    ) : (
                        <div style="padding: 0.75rem; color: #666;">SandboxedApp: no content URL resolved</div>
                    )}
                    {this.showLocalDevHelp ? (
                        <div
                            style={{
                                position: 'absolute',
                                inset: 0,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: '1rem',
                                background: 'rgba(255,255,255,0.96)',
                                zIndex: 1,
                            }}>
                            <div style={{ maxWidth: '28rem', textAlign: 'center', color: '#333' }}>
                                <div style={{ fontWeight: 600, marginBottom: '0.5rem' }}>
                                    Dev server certificate not trusted yet
                                </div>
                                <div style={{ fontSize: '0.9rem', marginBottom: '1rem', lineHeight: 1.4 }}>
                                    Make sure <code>npm run dev</code> is running. If it is, open the app
                                    in a new tab once to trust the local HTTPS certificate, then click Retry.
                                </div>
                                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <button type="button" onClick={this.openLocalDevInTab}>
                                        Open app in new tab
                                    </button>
                                    <button type="button" onClick={this.retryLocalDev}>
                                        Retry
                                    </button>
                                </div>
                            </div>
                        </div>
                    ) : null}
                </div>
            );
        }
        catch (e) {
            utils.error('SandboxedApp Render failed', e);
            return <div>SandboxedApp Failed to Render {e}</div>;
        }
    },
});
